<template>
  <!--
    noVNC example: lightweight example using minimal UI and features

    This is a self-contained file which doesn't import WebUtil or external CSS.

    Copyright (C) 2019 The noVNC Authors
    noVNC is licensed under the MPL 2.0 (see LICENSE.txt)
    This file is licensed under the 2-Clause BSD license (see LICENSE.txt).

    Connect parameters are provided in query string:
        http://example.com/?host=HOST&port=PORT&scale=true
    -->

  <!-- Always force latest IE rendering engine (even in intranet) &
                Chrome Frame. Remove this if you use the .htaccess -->
  <div class="container">
    <div id="top_bar">
      <div id="status">Loading</div>
      <div id="sendCtrlAltDelButton" v-on:click="sendCtrlAltDel">
        Send CtrlAltDel
      </div>
      <div id="pastePassword" @click="pastePassword">
        Paste password and confirm
      </div>
    </div>
    <div id="screen">
      <!-- This is where the remote screen will appear -->
    </div>
  </div>
</template>

<script type="module" crossorigin="anonymous">
// RFB holds the API to connect and communicate with a VNC server
import RFB from "./noVNC/core/rfb.js";
//import LoadScript from 'vue-plugin-load-script';

let rfb;
let desktopName;

export default {
  name: "VueNoVNC",
  props: ["host", "port", "password", "path", "ws", "username", "userpassword"],
  data() {
    return {};
  },
  methods: {
    // When this function is called we have
    // successfully connected to a server
    connectedToServer() {
      this.status("Connected to " + desktopName);
    },
    // This function is called when we are disconnected
    disconnectedFromServer(e) {
      if (e.detail.clean) {
        this.status("Disconnected");
      } else {
        this.status("Connection is closed");
        // console.log("Connection failed");
        // console.log("Host:" + this.host);
        // console.log("Port:" + this.port);
        // console.log("Password:" + this.password);
        // console.log("Path:" + this.path);
      }
    },
    // When this function is called, the server requires
    // credentials to authenticate
    credentialsAreRequired() {
      const password = this.password || prompt("Password Required:"); //TODO if we prompt for a password, it must not be clear text prompt
      rfb.sendCredentials({ password: password });
    },
    // When this function is called we have received
    // a desktop name from the server
    updateDesktopName(e) {
      desktopName = e.detail.name;
    },
    // Since most operating systems will catch Ctrl+Alt+Del
    // before they get a chance to be intercepted by the browser,
    // we provide a way to emulate this key sequence.
    sendCtrlAltDel() {
      rfb.sendCtrlAltDel();
      return false;
    },
    sendStringToVNC(msg) {
      for (let i = 0; i < msg.length; ++i) {
        let val = msg.charAt(i);
        rfb.sendKey(val.codePointAt(), "Key" + val);
      }
    },

    // This simply triggers a mouse click to the remote screen
    // and will then type-in the password and confirms it with return.
    // See further details here: https://vncdotool.readthedocs.io/en/78/rfbproto.html
    pastePassword() {
      this.sendStringToVNC(this.userpassword);
      rfb.sendKey(0xff0d, "Enter");
    },
    async pasteClipboard() {
      const text = await navigator.clipboard.readText();
      this.sendStringToVNC(text);
    },
    // Show a status text in the top bar
    status(text) {
      document.getElementById("status").textContent = text;
    },
    // This function extracts the value of one variable from the
    // query string. If the variable isn't defined in the URL
    // it returns the default value instead.
    readQueryVariable(name, defaultValue) {
      // A URL with a query parameter can look like this:
      // https://www.example.com?myqueryparam=myvalue
      //
      // Note that we use location.href instead of location.search
      // because Firefox < 53 has a bug w.r.t location.search
      const re = new RegExp(".*[?&]" + name + "=([^&#]*)"),
        match = document.location.href.match(re);

      if (match) {
        // We have to decode the URL since want the cleartext value
        return decodeURIComponent(match[1]);
      }

      return defaultValue;
    }
  },
  meta: [
    { charset: "utf-8" },
    { "http-equiv": "X-UA-Compatible", content: "IE=edge" }
  ],
  created() {
    /*
            let promise = document.createElement('script');
            promise.setAttribute('src', "noVNC/vendor/promise.js");
            document.head.appendChild(promise);

            let esModuleLoader = document.createElement('script');
            esModuleLoader.setAttribute('src', "noVNC/vendor/browser-es-module-loader/dist/browser-es-module-loader.js");
            document.head.appendChild(esModuleLoader);
            */
  },
  mounted() {
    //document.getElementById('sendCtrlAltDelButton').onclick = sendCtrlAltDel;

    // Read parameters specified in the URL query string
    // By default, use the host and port of server that served this file
    // const host = readQueryVariable('host', window.location.hostname);
    // let port = readQueryVariable('port', window.location.port);
    // const password = readQueryVariable('password');
    // const path = readQueryVariable('path', 'websockify');

    // | | |         | | |
    // | | | Connect | | |
    // v v v         v v v

    this.status("Connecting");

    // Build the websocket URL used to connect window.location.protocol === "https:"
    let winProtocol = "";
    if (window.location.protocol === "https:") {
      winProtocol = "wss";
    } else {
      winProtocol = "ws";
    }

    let url = this.ws ?? winProtocol;

    url += "://" + this.host;
    if (this.port) {
      url += ":" + this.port;
    }
    url += "/" + this.path;
    console.log("URL:" + url);

    // Creating a new RFB object will start a new connection
    rfb = new RFB(document.getElementById("screen"), url, {
      credentials: { password: this.password }
    });

    // Add listeners to important events from the RFB module
    rfb.addEventListener("connect", this.connectedToServer);
    rfb.addEventListener("disconnect", this.disconnectedFromServer);
    rfb.addEventListener("credentialsrequired", this.credentialsAreRequired);
    rfb.addEventListener("desktopname", this.updateDesktopName);

    // Set parameters that can be changed on an active connection
    rfb.viewOnly = this.readQueryVariable("view_only", false);
    rfb.scaleViewport = this.readQueryVariable("scale", false);
  }
};
</script>

<style lang="scss">
html {
  height: 100%;
}

.container {
  height: 100%;
  width: 100%;
}
#top_bar {
  background-color: #6e84a3;
  color: black;
  font: bold 12px Helvetica;
  padding: 6px 5px 4px 5px;
  border-bottom: 1px outset;
}

#status {
  color: white;
  text-align: center;
}
#sendCtrlAltDelButton {
  position: fixed;
  top: 0px;
  right: 0px;
  border: 1px outset;
  padding: 5px 5px 4px 5px;
  cursor: pointer;
  background-color: whitesmoke;
}
#pastePassword {
  position: fixed;
  top: 0px;
  border: 1px outset;
  padding: 5px 5px 4px 5px;
  cursor: pointer;
  background-color: whitesmoke;
}
#screen {
  flex: 1; /* fill remaining space */
  overflow: hidden;
  height: 100vh !important;
}
</style>
